import { useState, useEffect } from 'react';
import {
    Show,
    useRecordContext,
    useRefresh,
    useNotify,
    TopToolbar,
    ListButton,
    useDataProvider
} from 'react-admin';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField as MuiTextField,
    Alert,
    Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import RefundIcon from '@mui/icons-material/Undo';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import config from '../../config/config.json';
const host = config.host;

const token = localStorage.getItem('token');

const statusColors = {
    pending: '#ff9800',
    succeeded: '#4caf50',
    failed: '#f44336',
    canceled: '#9e9e9e',
    refunded: '#2196f3'
};

const statusIcons = {
    pending: <HourglassEmptyIcon />,
    succeeded: <CheckCircleIcon />,
    failed: <ErrorIcon />,
    canceled: <CancelIcon />,
    refunded: <MoneyOffIcon />
};

const statusLabels = {
    pending: 'In Attesa',
    succeeded: 'Completato',
    failed: 'Fallito',
    canceled: 'Annullato',
    refunded: 'Rimborsato'
};

// Dialog per rimborso
const RefundDialog = ({ open, onClose, transaction, onSuccess }) => {
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('requested_by_customer');
    const [loading, setLoading] = useState(false);
    const notify = useNotify();

    const maxAmount = transaction ? (transaction.amount / 100).toFixed(2) : 0;

    const handleRefund = async () => {
        setLoading(true);
        try {

            const apiUrl = host;

            const payload = {
                action: 'create-refund',
                paymentIntentId: transaction.stripe_payment_intent_id,
                reason
            };

            // Se specificato un importo, aggiungi al payload (in centesimi)
            if (amount && parseFloat(amount) > 0) {
                payload.amount = Math.round(parseFloat(amount) * 100);
            }

            const response = await axios.post(`${apiUrl}/stripe`, payload, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            notify('Rimborso creato con successo', { type: 'success' });
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Errore rimborso:', error);
            notify(error.response?.data?.message || 'Errore durante il rimborso', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>💰 Crea Rimborso</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Importo massimo rimborsabile: <strong>€{maxAmount}</strong>
                    </Alert>

                    <MuiTextField
                        label="Importo Rimborso (€)"
                        type="number"
                        fullWidth
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        helperText="Lascia vuoto per rimborso totale"
                        sx={{ mb: 2 }}
                        inputProps={{
                            step: '0.01',
                            min: '0',
                            max: maxAmount
                        }}
                    />

                    <MuiTextField
                        label="Motivo"
                        select
                        fullWidth
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        SelectProps={{
                            native: true
                        }}
                    >
                        <option value="requested_by_customer">Richiesto dal Cliente</option>
                        <option value="duplicate">Pagamento Duplicato</option>
                        <option value="fraudulent">Fraudolento</option>
                    </MuiTextField>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Annulla
                </Button>
                <Button
                    onClick={handleRefund}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={<RefundIcon />}
                >
                    {loading ? 'Elaborazione...' : 'Crea Rimborso'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Componente per visualizzare i dettagli
const TransactionDetails = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [refundDialogOpen, setRefundDialogOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);
    const refresh = useRefresh();
    const notify = useNotify();

    // Carica dati utente
    useEffect(() => {
        if (!record || !record.usersId) {
            setUser(null);
            return;
        }

        setLoadingUser(true);
        dataProvider.getOne('users', { id: record.usersId })
            .then(({ data }) => {
                setUser(data);
                setLoadingUser(false);
            })
            .catch((error) => {
                console.error('Errore caricamento utente:', error);
                setLoadingUser(false);
            });
    }, [record, dataProvider]);

    if (!record) return null;

    const status = record.status || 'pending';
    const color = statusColors[status];
    const icon = statusIcons[status];
    const label = statusLabels[status];
    const amount = (record.amount / 100).toFixed(2);
    const currency = (record.currency || 'EUR').toUpperCase();

    const canRefund = status === 'succeeded';

    const openStripePayment = () => {
        const stripeUrl = `https://dashboard.stripe.com/test/payments/${record.stripe_payment_intent_id}`;
        window.open(stripeUrl, '_blank');
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {/* Status Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Status
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                                <Chip
                                    icon={icon}
                                    label={label}
                                    sx={{
                                        backgroundColor: color,
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '1rem',
                                        padding: '20px 10px',
                                        '& .MuiChip-icon': {
                                            color: 'white',
                                            fontSize: '1.5rem'
                                        }
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Importo Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ height: '100%', borderLeft: '4px solid #2196f3' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Importo
                            </Typography>
                            <Typography variant="h3" sx={{ mt: 2, color: '#2196f3', fontWeight: 'bold' }}>
                                €{amount}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {currency}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Utente Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ height: '100%', borderLeft: '4px solid #ff9800' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                👤 Utente
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                {loadingUser ? (
                                    <Typography variant="body2" color="textSecondary">Caricamento...</Typography>
                                ) : user ? (
                                    <>
                                        <Typography variant="h6" fontWeight="bold">
                                            {user.nome || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                            {user.email}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            ID: {user.id}
                                        </Typography>
                                    </>
                                ) : record.usersId ? (
                                    <Typography variant="body1">User ID: {record.usersId}</Typography>
                                ) : (
                                    <Typography variant="body1" color="textSecondary">Guest</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Azioni Card */}
                <Grid item xs={12} md={3}>
                    <Card sx={{ height: '100%', borderLeft: '4px solid #9c27b0' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Azioni
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={openStripePayment}
                                    fullWidth
                                >
                                    Apri su Stripe
                                </Button>
                                {canRefund && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<RefundIcon />}
                                        onClick={() => setRefundDialogOpen(true)}
                                        fullWidth
                                    >
                                        Crea Rimborso
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Dettagli Transazione */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                📋 Dettagli Transazione
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">ID Transazione</Typography>
                                    <Typography variant="body1" fontWeight="bold">{record.id}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">Payment Intent ID</Typography>
                                    <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                        {record.stripe_payment_intent_id}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">Descrizione</Typography>
                                    <Typography variant="body1">{record.description || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">Utente</Typography>
                                    {loadingUser ? (
                                        <Typography variant="body1" color="textSecondary">Caricamento...</Typography>
                                    ) : user ? (
                                        <Box>
                                            <Typography variant="body1" fontWeight="bold">
                                                {user.nome || user.email}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {user.email}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {user.id}
                                            </Typography>
                                        </Box>
                                    ) : record.usersId ? (
                                        <Typography variant="body1">User ID: {record.usersId}</Typography>
                                    ) : (
                                        <Typography variant="body1" color="textSecondary">Guest</Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">Data Creazione</Typography>
                                    <Typography variant="body1">
                                        {new Date(record.created_at).toLocaleString('it-IT')}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="textSecondary">Ultimo Aggiornamento</Typography>
                                    <Typography variant="body1">
                                        {new Date(record.updated_at).toLocaleString('it-IT')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Metadata */}
                {record.metadata && (
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    🔍 Metadata
                                </Typography>
                                <Divider sx={{ my: 2 }} />
                                <Box
                                    component="pre"
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        padding: 2,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        fontSize: '0.85rem',
                                        fontFamily: 'monospace'
                                    }}
                                >
                                    {JSON.stringify(JSON.parse(record.metadata), null, 2)}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* Dialog Rimborso */}
            <RefundDialog
                open={refundDialogOpen}
                onClose={() => setRefundDialogOpen(false)}
                transaction={record}
                onSuccess={refresh}
            />
        </Box>
    );
};

const ShowActions = () => (
    <TopToolbar>
        <ListButton />
    </TopToolbar>
);

export const StripeTransactionsShow = (props) => {
    return (
        <Show {...props} actions={<ShowActions />} title="Dettaglio Transazione">
            <TransactionDetails />
        </Show>
    );
};
